import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  InputSignal,
  InputSignalWithTransform,
  Signal
} from '@angular/core';

interface OrderThankYouCartDeliveryField {
  name: string;
  value: string;
}

interface ParsedDeliveryDate {
  date: string;
  time: string;
}

@Component({
  selector: 'app-order-thank-you-card',
  templateUrl: './order-thank-you-card.component.html',
  styleUrl: './order-thank-you-card.component.scss',
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderThankYouCardComponent {
  orderNumber: InputSignal<string> = input('123456789');

  // User Data
  userEmail: InputSignal<string> = input('youremail@gmail.com');

  paymentMethod: InputSignal<string> = input('Credit Card');
  userCard: InputSignalWithTransform<string, string> = input('4567', {
    transform: (value: string): string => value.slice(-4)
  });

  // Delivery Data
  deliveryDate: InputSignal<string> = input<string>(''); // Expect Formatted Date
  deliveryAddress: InputSignal<string> = input<string>('');
  deliveryDropoff: InputSignal<string> = input<string>('');
  deliveryDropoffInstructions: InputSignal<string> = input<string>('');
  isPickup: InputSignal<boolean> = input<boolean>(false); // Determines if order is pickup or delivery

  parsedDeliveryDate: Signal<ParsedDeliveryDate> = computed(() => {
    const dateString = this.deliveryDate();
    return this.parseDeliveryDate(dateString);
  });

  deliveryTypeLabel: Signal<string> = computed(() => {
    return this.isPickup() ? 'Pickup:' : 'Delivery:';
  });

  deliveryFields: Signal<OrderThankYouCartDeliveryField[]> = computed(() => [
    {
      name: this.isPickup() ? 'Pickup Address:' : 'Delivery Address:',
      value: this.deliveryAddress()
    },
    {
      name: 'Drop off:',
      value: this.deliveryDropoff()
    },
    {
      name: 'Drop Off Instructions:',
      value: this.deliveryDropoffInstructions()
    }
  ]);

  private parseDeliveryDate(dateString: string): ParsedDeliveryDate {
    if (!dateString) {
      return { date: '', time: '' };
    }

    // Check if it ends with 'asap' or 'ASAP'
    const asapRegex = /\s+asap$/i;
    if (asapRegex.test(dateString)) {
      let date = dateString.replace(asapRegex, '').trim();
      // Remove year if present (e.g., "Friday, November 07 2025" -> "Friday, November 07")
      date = date.replace(/\s+\d{4}$/, '').trim();
      return { date, time: 'ASAP' };
    }

    // Check if it contains a time range (e.g., "Friday, November 07 12:00 - 1:00 PM")
    const timeRangeRegex =
      /(.+)\s+(\d{1,2}:\d{2}\s*-\s*\d{1,2}:\d{2}\s*(?:AM|PM|am|pm)?)/;
    const rangeMatch = dateString.match(timeRangeRegex);

    if (rangeMatch) {
      let date = rangeMatch[1].trim();
      date = date.replace(/\s+\d{4}$/, '').trim();
      return {
        date,
        time: `Time: ${rangeMatch[2].trim()}`
      };
    }

    // Check for a single time at the end (e.g., "Wednesday, May 06 6:05 PM")
    const singleTimeRegex = /^(.+)\s+(\d{1,2}:\d{2}\s*(?:AM|PM|am|pm))$/i;
    const singleTimeMatch = dateString.match(singleTimeRegex);

    if (singleTimeMatch) {
      let date = singleTimeMatch[1].trim();
      date = date.replace(/\s+\d{4}$/, '').trim();
      return {
        date,
        time: singleTimeMatch[2].trim()
      };
    }

    // If no time information found, remove year if present and return as date
    let date = dateString.replace(/\s+\d{4}$/, '').trim();
    return { date, time: '' };
  }
}
